import React from 'react';
import { FC } from 'react';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import DatePicker from 'react-datepicker';
import styles from './CalendarHeader.module.css';
import { FlexCalSummary } from '../../utils/types';
import { DateTime } from 'luxon';
import { navHeaderClick, ViewType } from '../../utils/enums';
import getInitialDate from '../../utils/helpers/getInitialDate';
import { ok } from 'assert';

type Props = {
  handleNavHeaderClick: (clickType: navHeaderClick) => void;
  currentView: ViewType;
  currentRange: string;
  flexCal: FlexCalSummary;
  datePickerDate?: DateTime;
  setDatePickerDate: React.Dispatch<React.SetStateAction<DateTime | undefined>>;
  isPrevDisabled: boolean;
  isNextDisabled: boolean;
  isEventClicked: boolean | undefined;
  canNavNext: boolean;
  canNavPrev: boolean;
};

const CalendarHeader: FC<Props> = ({
  handleNavHeaderClick,
  currentView,
  currentRange,
  datePickerDate,
  setDatePickerDate,
  flexCal,
  isPrevDisabled,
  isNextDisabled,
  isEventClicked,
  canNavNext,
  canNavPrev,
}) => {
  return (
    <div
      className={`flex flex-row items-center justify-center ${
        isEventClicked ? 'gap-4' : 'lg:gap-6 gap-2'
      }`}
    >
      <div
        className={`flex flex-row bg-white border-2 divide-x-2 rounded-lg divide-dark-grey order-1 border-dark-grey ${
          isEventClicked ? 'hidden' : ''
        }`}
      >
        <button
          className={`lg:w-20 w-12 lg:py-2 py-1 rounded-l-md ${
            currentView === ViewType.Day ? 'bg-selected' : ''
          }`}
          onClick={() => handleNavHeaderClick(navHeaderClick.DayView)}
          disabled={currentView === ViewType.Day}
        >
          Day
        </button>
        <button
          className={`lg:w-20 w-12 lg:py-2 py-1 rounded-r-md ${
            currentView === ViewType.Week ? 'bg-selected' : ''
          }`}
          onClick={() => handleNavHeaderClick(navHeaderClick.WeekView)}
          disabled={currentView === ViewType.Week}
        >
          Week
        </button>
      </div>
      <div
        className={`flex flex-row bg-white border-2 divide-x-2 rounded-lg divide-dark-grey border-dark-grey order-3 lg:order-2 ${
          isEventClicked ? 'hidden' : ''
        }`}
      >
        <button
          className={`px-2 py-1 lg:px-3 lg:py-2 rounded-l-md ${
            canNavPrev ? '' : 'bg-selected cursor-default'
          }`}
          onClick={() => handleNavHeaderClick(navHeaderClick.Prev)}
          //disabled={isPrevDisabled}
        >
          <BsChevronLeft size={25} />
        </button>
        <button
          className={`px-2 py-1 lg:px-3 lg:py-2 rounded-r-md ${
            canNavNext ? '' : 'bg-selected cursor-default'
          }`}
          onClick={() => handleNavHeaderClick(navHeaderClick.Next)}
          //disabled={isNextDisabled}
        >
          <BsChevronRight size={25} />
        </button>
      </div>
      <button
        className={`px-2 py-2 rounded-l-md ${
          isPrevDisabled ? 'bg-selected' : ''
        } ${isEventClicked ? 'order-1' : 'hidden'}`}
        onClick={() => handleNavHeaderClick(navHeaderClick.Prev)}
        disabled={isPrevDisabled}
      >
        <BsChevronLeft size={15} />
      </button>
      <h3
        className={`text-center ${
          isEventClicked
            ? 'text-xl w-auto whitespace-no-wrap order-2'
            : 'lg:text-3xl text-md w-3/12 lg:order-3 order-2'
        }`}
      >
        {currentRange}
      </h3>
      <button
        className={`px-2 py-2 rounded-r-md ${
          isNextDisabled ? 'bg-selected' : ''
        } ${isEventClicked ? 'order-3' : 'hidden'}`}
        onClick={() => handleNavHeaderClick(navHeaderClick.Next)}
        disabled={isNextDisabled}
      >
        <BsChevronRight size={15} />
      </button>
      {/* <div
        className={`bg-white border-2 rounded-lg border-dark-grey order-none lg:order-4 lg:block hidden ${
          showToday ? '' : 'invisible'
        } ${isEventClicked ? 'lg:hidden' : ''}`}
      >
        <button
          className={`lg:px-6 lg:py-2 px-3 py-1 rounded-md ${
            isToday ? 'bg-selected' : ''
          }`}
          onClick={() => handleNavHeaderClick(navHeaderClick.Today)}
          disabled={isToday}
        >
          Today
        </button>
      </div> */}
      <div
        className={`hidden lg:block order-none lg:order-5 ${
          isEventClicked ? 'lg:hidden' : ''
        }`}
      >
        <DatePicker
          className="p-2 bg-white border-2 rounded-lg border-dark-grey"
          popperClassName={styles.lift}
          selected={datePickerDate?.toJSDate()}
          onChange={(date) =>
            date !== null ? setDatePickerDate!(DateTime.fromJSDate(date!)) : ok
          }
          minDate={getInitialDate(flexCal).toJSDate()}
          maxDate={
            flexCal?.evergreen
              ? DateTime.fromISO(flexCal.endDate)
                  .endOf('week')
                  .minus({ days: 1 })
                  .toJSDate()
              : DateTime.fromISO(flexCal?.endDate).toJSDate()
          }
          placeholderText="Enter date"
        />
      </div>
    </div>
  );
};

export default CalendarHeader;
